import React, { createContext, useMemo } from 'react'
import { useProjects } from 'services/projects'
import { useCurrentAccount, getUserId } from 'services/user'
import { hasRights } from 'utils'
import localStore from 'store'

const apiContext = createContext()

// Usecases when login:
// [x] (1) User has no token in local storage -> await token from auth0, check if user accepted a project (local storage set)
// [x]   (1.1) First login -> await auth0 user -> add user to server -> then update user locally -> then fetch projects
// [x]   (1.2) Normal login -> fetch token (TAKES LONG!) -> fetch user and projects from server asynchronously
// [x] (2) User has token in local storage -> fetch user, projects and auth0 asynchronously
// [x] (3) User has token and GRAPQHL in local storage -> render page and refetch in background asynchronously (to update e.g. outdated information)
// [ ] + [1,2,3 any case] after token, if accept_project_id set in local storage -> accept project and redirect to it

const ApiContext = ({ children }) => {
  const { project, projects } = useProjects()
  const { user, userId } = useCurrentAccount()
  const locale = localStore.get('app.settings.locale')

  // useMemo => Avoid rerender on change of auth0 provider (background syncing)
  return useMemo(() => {
    const {
      projectMemberships,
      projectInvitations,
      projectDocuments,
      investors,
      stakeholders,
      shares,
      payOuts,
      capTables,
      capTable,
      company,
      esops,
      esopGrants,
      proposedEsopGrants,
      buyOuts,
      investments,
      events,
    } = project || {}
    const { participations } = capTable || {}
    const { round } = (capTable && capTable.termsheet) || {}
    const rounds = capTables ? capTables.map((c) => c.termsheet.round) : []
    const membership = project
      ? project.projectMemberships.find((m) => m.userAccount.userId === userId)
      : null
    const disableEdit = !hasRights(membership, 'EDITOR')

    const provides = {
      project,
      membership,
      projects,
      user,
      userId: getUserId(),

      disableEdit,
      locale: locale || 'en-US',

      // Easier access
      company,
      projectMemberships,
      projectInvitations,
      projectDocuments,
      investors,
      stakeholders,
      shares,
      payOuts,
      capTables,
      rounds,
      esops,
      esopGrants,
      proposedEsopGrants,
      buyOuts,
      investments,
      events,

      // latest
      capTable,
      round,
      participations,
    }

    return <apiContext.Provider value={provides}>{children}</apiContext.Provider>
  }, [project, projects, user, locale])
}

export default ApiContext
export { apiContext }
export const DEFAULT_FETCH_POLICY = 'cache-and-network' // to ensure cache is always updated (even though persistend in browser)
